import { Link } from "react-router-dom";
import LegalLayout, { Section } from "@/components/LegalLayout";

export default function Terms() {
  return (
    <LegalLayout
      title="Terms of Service"
      testid="terms-page"
      path="/terms"
      intro="These Terms of Service govern your access to and use of Videoileo (the “Service”), an AI tool for creating faceless videos and scheduling them to social platforms. By creating an account or using the Service, you agree to these Terms."
    >
      <Section title="1. Acceptance of terms">
        By accessing or using Videoileo you confirm that you have read, understood, and agree to be bound by these
        Terms, our <Link to="/privacy" className="text-blue-400 hover:text-blue-300 underline">Privacy Policy</Link>,{" "}
        <Link to="/acceptable-use" className="text-blue-400 hover:text-blue-300 underline">Acceptable Use Policy</Link>, and{" "}
        <Link to="/legal" className="text-blue-400 hover:text-blue-300 underline">Content &amp; Copyright Policy</Link>. If
        you do not agree, do not use the Service.
      </Section>

      <Section title="2. Eligibility">
        You must be at least 16 years old (or the age of digital consent in your country) and able to form a binding
        contract to use Videoileo. If you use the Service on behalf of an organization, you represent that you are
        authorized to bind that organization to these Terms.
      </Section>

      <Section title="3. Accounts &amp; security">
        You are responsible for maintaining the confidentiality of your login credentials and for all activity under
        your account. You agree to provide accurate information and to notify us promptly of any unauthorized use. You
        may update your profile or password at any time in Settings.
      </Section>

      <Section title="4. Credits &amp; purchases">
        The Service uses a credit system to power AI generation (scripts, voiceovers, and visuals). Credits are consumed
        when you generate content. Credit packages and any pricing shown in the app may change. Credits are licenses to
        use the Service and have no cash value; consumed credits are non-refundable except where required by law. Any
        demo or simulated checkout adds credits without a real charge and confers no payment obligation.
      </Section>

      <Section title="5. Your content &amp; ownership">
        You retain ownership of the prompts and topics you submit and, subject to the terms of our AI providers, the
        outputs you generate. You are solely responsible for your content. By using the Service, you grant Videoileo a
        limited license to process, store, and transmit your inputs and outputs as necessary to operate and provide the
        Service (including sending them to AI subprocessors as described in our Privacy Policy).
      </Section>

      <Section title="6. AI-generated content">
        AI output may be inaccurate, generic, or unintentionally similar to existing works. Videoileo does not guarantee
        that generated content is original, accurate, or free of third-party rights. You must review and, where
        necessary, edit and clear content before publishing or commercial use.
      </Section>

      <Section title="7. Third-party platforms">
        When you schedule or publish to YouTube, TikTok, Instagram, or any other service, you must comply with that
        platform's terms and policies. Videoileo is independent and not affiliated with those platforms. We are not
        responsible for platform actions, including content removal or account suspension.
      </Section>

      <Section title="8. Prohibited use">
        You agree not to misuse the Service. Prohibited activities are described in our{" "}
        <Link to="/acceptable-use" className="text-blue-400 hover:text-blue-300 underline">Acceptable Use Policy</Link>,
        which is incorporated into these Terms. Violations may result in suspension or termination.
      </Section>

      <Section title="9. Disclaimers">
        The Service is provided “as is” and “as available” without warranties of any kind, express or implied, including
        merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service will
        be uninterrupted, error-free, or secure.
      </Section>

      <Section title="10. Limitation of liability">
        To the maximum extent permitted by law, Videoileo and its affiliates will not be liable for any indirect,
        incidental, special, consequential, or punitive damages, or any loss of profits, revenue, data, or goodwill,
        arising from your use of the Service. Our aggregate liability will not exceed the amount you paid us in the 12
        months preceding the claim.
      </Section>

      <Section title="11. Indemnification">
        You agree to indemnify and hold harmless Videoileo from any claims, damages, or expenses (including reasonable
        legal fees) arising from your content, your use of the Service, or your violation of these Terms or any
        third-party rights, including copyright and trademark claims.
      </Section>

      <Section title="12. Termination">
        You may stop using the Service at any time. We may suspend or terminate your access if you violate these Terms or
        the Acceptable Use Policy, or to comply with law. Upon termination, your right to use the Service ends.
      </Section>

      <Section title="13. Changes to these terms">
        We may update these Terms from time to time. Material changes will be reflected by the “Last updated” date.
        Continued use after changes take effect constitutes acceptance.
      </Section>

      <Section title="14. Governing law">
        These Terms are governed by the laws of your applicable jurisdiction, without regard to conflict-of-law rules.
        Nothing here limits any non-waivable statutory rights you may have as a consumer.
      </Section>
    </LegalLayout>
  );
}
